import React, { useMemo, useState } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, IconButton, InputAdornment, Modal, TextField, Tooltip } from "@mui/material";
import classNames from "classnames";
import { ProgressOverlay } from "components";
import { useDevice, useDidUpdate } from "hooks";
import { useDataRoomsActions, usePlatformsSelect } from "store";
import { IModals } from "store/types/modals";

import { Login } from "./Login";
import { RoomConnectMobile } from "./RoomConnectMobile";

import "./RoomConnect.styles.scss";

type Props = {
  onClose: () => void;
} & IModals["dataroomConnect"];

export const RoomConnect: React.FC<Props> = ({ isOpen, onClose, afterSubmit, platforms: platformsFromProps }) => {
  const { isMobile } = useDevice();
  const [step, setStep] = useState<number>(1);
  const [platformId, setPlatformId] = useState<string | null>(null);
  const [platformName, setPlatformName] = useState<string | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<{ [key: string]: string | boolean | null } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const { setConnectMessageAction } = useDataRoomsActions();
  const cachedPlatforms = usePlatformsSelect();

  // Use platforms from props (fetched before modal opens) > cached
  const platforms = platformsFromProps || cachedPlatforms;

  // Find the selected platform
  const selectedPlatformData = useMemo(() => {
    if (!platformId || !platforms) return null;
    return platforms.find((p) => p.id === platformId);
  }, [platformId, platforms]);

  const filteredPlatforms = useMemo(() => {
    if (!platforms || !searchTerm) return platforms;
    const searchLower = searchTerm.toLowerCase();
    return platforms.filter((platform) => platform.name.toLowerCase().includes(searchLower));
  }, [platforms, searchTerm]);

  useDidUpdate(() => {
    setConnectMessageAction(null);
    if (!isOpen) {
      if (step !== 1) {
        setStep(1);
      }

      if (platformId) {
        setPlatformId(null);
        setPlatformName(null);
      }
      setSearchTerm("");
    }
  }, [isOpen]);

  // Render mobile version
  if (isMobile) {
    return (
      <RoomConnectMobile isOpen={isOpen} onClose={onClose} afterSubmit={afterSubmit} platforms={platformsFromProps} />
    );
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box sx={style}>
        <div className="room-connect">
          <div className="room-connect__head">
            <span>Connect to Data Room</span>
            <IconButton onClick={onClose}>
              <ClearIcon
                sx={{
                  color: "#0A3142"
                }}
              />
            </IconButton>
          </div>
          <ProgressOverlay spinnerProps={{ size: 100 }} className="room-content__progress" inProgress={loading}>
            <div className="room-connect__body">
              <div
                className={classNames("step", {
                  "step--active": step === 1
                })}
              >
                <div className="step__index">
                  <div className="point" />
                  <div className="row" />
                </div>
                <div className="step__content">
                  <div className="step__title" onClick={() => setStep(1)}>
                    <span>Select Platform</span>
                    {selectedPlatformData && (
                      <div className="selected-platform-display">
                        <img
                          src={selectedPlatformData.logo}
                          alt={selectedPlatformData.name}
                          className="selected-platform-logo"
                        />
                        <span className="selected-platform-name">{selectedPlatformData.name}</span>
                      </div>
                    )}
                  </div>
                  {step === 1 && (
                    <>
                      <div className="platform-search">
                        <TextField
                          placeholder="Search platforms..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "transparent",
                              border: "1px solid transparent",
                              borderRadius: "6px",
                              color: "rgba(10, 49, 66, 0.7)",
                              transition: "all 0.3s ease-in-out",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.15)",
                                borderColor: "rgba(186, 199, 206, 0.4)"
                              },
                              "&.Mui-focused": {
                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                borderColor: "rgba(186, 199, 206, 0.6)",
                                color: "rgba(10, 49, 66, 0.9)",
                                cursor: "text !important"
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none"
                              }
                            },
                            "& .MuiInputBase-input": {
                              padding: "6px 12px",
                              fontSize: "14px",
                              cursor: "pointer",
                              "&:focus": {
                                cursor: "text !important"
                              },
                              "&::placeholder": {
                                color: "rgba(10, 49, 66, 0.4)",
                                opacity: 1
                              }
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon
                                  sx={{
                                    color: "rgba(10, 49, 66, 0.5)",
                                    fontSize: "20px",
                                    transition: "color 0.2s ease-in-out"
                                  }}
                                />
                              </InputAdornment>
                            )
                          }}
                          onFocus={(e) => {
                            // Change icon color on focus
                            const icon = e.currentTarget.querySelector(".MuiInputAdornment-root .MuiSvgIcon-root");
                            if (icon) {
                              (icon as HTMLElement).style.color = "#0a3142";
                            }
                          }}
                          onBlur={(e) => {
                            // Reset icon color on blur
                            const icon = e.currentTarget.querySelector(".MuiInputAdornment-root .MuiSvgIcon-root");
                            if (icon) {
                              (icon as HTMLElement).style.color = "rgba(10, 49, 66, 0.5)";
                            }
                          }}
                        />
                      </div>
                      <div className="step__body">
                        <div className="cards-grid">
                          {filteredPlatforms?.map((item) => (
                            <div
                              onClick={() => {
                                if (platformId === item.id) {
                                  // If clicking the same platform that's already selected, just go to step 2
                                  setStep(2);
                                } else {
                                  // Select new platform and go to step 2
                                  setPlatformName(item.name);
                                  setPlatformId(item.id);
                                  setSelectedPlatform(item.extra);
                                  setStep(2);
                                }
                              }}
                              key={item.id}
                              className={classNames("card", {
                                "card--selected": platformId === item.id
                              })}
                            >
                              <img src={item.logo} className="card__logo" />
                              <Tooltip title={item.name} arrow placement="top">
                                <span className="card__titile">{item.name}</span>
                              </Tooltip>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="step__footer">
                        <Button onClick={() => setStep(2)} disabled={!platformId} variant="contained">
                          Next
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div
                className={classNames("step", {
                  "step--active": step === 2
                })}
              >
                <div className="step__index">
                  <div className="point" />
                  {/* <div className="row" /> */}
                </div>
                <div className="step__content">
                  <div
                    className="step__title"
                    onClick={() => {
                      if (platformId) {
                        setStep(2);
                      }
                    }}
                  >
                    Enter Credentials
                  </div>
                  {platformId && platformName && (
                    <Login
                      setLoading={setLoading}
                      closeModal={onClose}
                      platformId={platformId}
                      platformName={platformName}
                      extra={selectedPlatform}
                      setStep={setStep}
                      resetSocket={() => setConnectMessageAction(null)}
                      afterSubmit={afterSubmit}
                    />
                  )}
                </div>
              </div>
            </div>
          </ProgressOverlay>
        </div>
      </Box>
    </Modal>
  );
};

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  outline: "none"
};
